import {
  AUTHORING_PATH_FIELD_MODE_DEFINITIONS,
  USER_DATA_PATH_ROOT,
  SYSTEM_DATA_PATH_ROOT,
  authoringPathDirname,
  classifyAuthoringPath,
  resolveUserPath,
  retokenizeUserPath,
  isHttpPathUrl,
  toAbsoluteSitePath,
  resolveResourcePath,
  type AuthoringPathFieldMode,
} from "./pathContract";

export const EDITOR_FILE_BROWSER_SCOPES = [
  "site",
  "user",
  "system",
] as const;

export type EditorFileBrowserScope =
  (typeof EDITOR_FILE_BROWSER_SCOPES)[number];

export const EDITOR_FILE_BROWSER_FILTERS = [
  "all",
  "assessment",
  "report",
  "automation",
  "image",
  "media",
  "agent",
] as const;

export type EditorFileBrowserFilter =
  (typeof EDITOR_FILE_BROWSER_FILTERS)[number];

export interface EditorFileBrowserEntry {
  id?: string;
  name: string;
  path: string;
  type: string;
  length?: number | null;
  metadata?: Record<string, unknown>;
}

export interface EditorFileBrowserDialogProps {
  path?: string | null;
  mode?: AuthoringPathFieldMode;
  scopes?: readonly EditorFileBrowserScope[];
  defaultScope?: EditorFileBrowserScope;
  previewCandidateId?: string | null;
  contentTypeFilter?: EditorFileBrowserFilter;
  includeContentTypes?: readonly string[];
  excludeContentTypes?: readonly string[];
  title?: string | null;
  description?: string | null;
  resourceDefinitionPath?: string | null;
}

export interface EditorFileBrowserSelection {
  path: string;
  scope: EditorFileBrowserScope;
  kind: "file" | "folder";
  entry: EditorFileBrowserEntry | null;
}

export interface EditorFileBrowserScopeLocation {
  scope: EditorFileBrowserScope;
  label: string;
  storedRoot: string;
  loadingRoot: string | null;
  candidateRequired: boolean;
}

export interface EditorFileBrowserBreadcrumb {
  label: string;
  path: string;
}

export const EDITOR_FILE_BROWSER_FILTER_PATTERNS: Readonly<
  Record<EditorFileBrowserFilter, readonly string[]>
> = {
  all: [],
  assessment: [
    "application/vnd.pivotalgroup.resource-definition.assessment+json",
  ],
  report: [
    "application/vnd.pivotalgroup.resource-definition.report+json",
  ],
  automation: [
    "application/vnd.pivotalgroup.automation+json",
    "application/vnd.pivotalgroup.automation+binary",
  ],
  image: ["image/*"],
  media: ["image/*", "audio/*", "video/*", "application/pdf"],
  agent: [
    "application/vnd.pivotalgroup.agent+json",
    "application/vnd.pivotalgroup.agent.flow+json",
  ],
};

export function isEditorFileBrowserDirectory(
  entry: Pick<EditorFileBrowserEntry, "type">,
): boolean {
  return entry.type === "text/directory";
}

export function matchesEditorFileBrowserContentType(
  contentType: string | null | undefined,
  patterns: readonly string[],
): boolean {
  if (!contentType || !patterns.length) return false;

  return patterns.some((pattern) => {
    if (pattern === "*") return true;
    if (pattern.endsWith("/*")) {
      return contentType.startsWith(pattern.slice(0, -1));
    }
    return contentType === pattern;
  });
}

export function editorFileBrowserEntryMatchesFilter(
  entry: EditorFileBrowserEntry,
  includePatterns: readonly string[],
  excludePatterns: readonly string[] = [],
): boolean {
  if (
    excludePatterns.length
    && matchesEditorFileBrowserContentType(entry.type, excludePatterns)
  ) {
    return false;
  }
  return !includePatterns.length
    || matchesEditorFileBrowserContentType(entry.type, includePatterns);
}

export function resolveEditorFileBrowserScope(
  scope: EditorFileBrowserScope,
  previewCandidateId?: string | null,
): EditorFileBrowserScopeLocation {
  if (scope === "user") {
    const candidateId = previewCandidateId?.trim();
    let loadingRoot: string | null = null;
    if (candidateId) {
      try {
        loadingRoot = resolveUserPath(USER_DATA_PATH_ROOT, candidateId);
      } catch {
        loadingRoot = null;
      }
    }
    return {
      scope,
      label: "Current candidate",
      storedRoot: USER_DATA_PATH_ROOT,
      loadingRoot,
      candidateRequired: !loadingRoot,
    };
  }

  if (scope === "system") {
    return {
      scope,
      label: "System",
      storedRoot: SYSTEM_DATA_PATH_ROOT,
      loadingRoot: SYSTEM_DATA_PATH_ROOT,
      candidateRequired: false,
    };
  }

  return {
    scope,
    label: "Site",
    storedRoot: "/",
    loadingRoot: "/",
    candidateRequired: false,
  };
}

function isAtOrBelow(path: string, root: string): boolean {
  if (root === "/") return path.startsWith("/");
  return path === root || path.startsWith(`${root}/`);
}

export function isEditorFileBrowserPathAllowed(
  path: string,
  scope: EditorFileBrowserScope,
  previewCandidateId?: string | null,
): boolean {
  const absolute = toAbsoluteSitePath(path);
  if (
    !absolute
    || isHttpPathUrl(absolute)
    || absolute.includes("\\")
    || absolute.split("/").some((segment) => segment === "." || segment === "..")
  ) {
    return false;
  }
  const location = resolveEditorFileBrowserScope(scope, previewCandidateId);

  if (scope === "site") {
    return absolute !== "/data" && !absolute.startsWith("/data/");
  }

  return isAtOrBelow(absolute, location.storedRoot)
    || Boolean(
      location.loadingRoot
      && isAtOrBelow(absolute, location.loadingRoot),
    );
}

export function toEditorFileBrowserStoredPath(
  path: string,
  scope: EditorFileBrowserScope,
  previewCandidateId?: string | null,
): string {
  const absolute = toAbsoluteSitePath(path);
  if (scope !== "user") return absolute;
  if (!previewCandidateId?.trim()) return absolute;
  try {
    return toAbsoluteSitePath(
      retokenizeUserPath(absolute, previewCandidateId),
    );
  } catch {
    return absolute;
  }
}

export function toEditorFileBrowserLoadingPath(
  storedPath: string,
  scope: EditorFileBrowserScope,
  previewCandidateId?: string | null,
): string | null {
  const absolute = toAbsoluteSitePath(storedPath);
  if (scope !== "user") {
    return isEditorFileBrowserPathAllowed(absolute, scope, previewCandidateId)
      ? absolute
      : null;
  }

  const location = resolveEditorFileBrowserScope(scope, previewCandidateId);
  if (!location.loadingRoot) return null;

  let resolved: string;
  try {
    resolved = resolveUserPath(absolute, previewCandidateId);
  } catch {
    return null;
  }
  return isEditorFileBrowserPathAllowed(resolved, scope, previewCandidateId)
    ? resolved
    : null;
}

export function inferEditorFileBrowserScope(
  path: string | null | undefined,
  fallback: EditorFileBrowserScope = "site",
): EditorFileBrowserScope {
  const scope = classifyAuthoringPath(path, { allowHttpUrls: false }).scope;
  if (scope === "user" || scope === "system" || scope === "site") return scope;
  return fallback;
}

export function initialEditorFileBrowserFolder(
  path: string | null | undefined,
  mode: AuthoringPathFieldMode,
  scope: EditorFileBrowserScope,
  previewCandidateId?: string | null,
  resourceDefinitionPath?: string | null,
): string {
  const location = resolveEditorFileBrowserScope(scope, previewCandidateId);
  if (!path) return location.storedRoot;

  const resolvedPath = scope === "site"
    ? resolveResourcePath(path, resourceDefinitionPath)
    : path;
  const tokenized = scope === "user"
    ? toEditorFileBrowserStoredPath(resolvedPath, scope, previewCandidateId)
    : toAbsoluteSitePath(resolvedPath);
  const modeDefinition = AUTHORING_PATH_FIELD_MODE_DEFINITIONS[mode];
  const candidateFolder = modeDefinition.selection === "file"
    || modeDefinition.preserveFilename
    ? authoringPathDirname(tokenized)
    : tokenized;
  const folder = candidateFolder || location.storedRoot;

  const loadingPath = toEditorFileBrowserLoadingPath(
    folder,
    scope,
    previewCandidateId,
  );
  if (loadingPath || (scope === "user" && location.candidateRequired)) {
    return isEditorFileBrowserPathAllowed(folder, scope, previewCandidateId)
      ? folder
      : location.storedRoot;
  }

  return location.storedRoot;
}

export function buildEditorFileBrowserBreadcrumbs(
  storedPath: string,
  scope: EditorFileBrowserScope,
): EditorFileBrowserBreadcrumb[] {
  const location = resolveEditorFileBrowserScope(scope);
  const absolute = toAbsoluteSitePath(storedPath);
  if (!isAtOrBelow(absolute, location.storedRoot)) {
    return [{ label: location.label, path: location.storedRoot }];
  }

  const suffix = absolute.slice(location.storedRoot.length)
    .split("/")
    .filter(Boolean);
  const breadcrumbs: EditorFileBrowserBreadcrumb[] = [
    { label: location.label, path: location.storedRoot },
  ];
  let path = location.storedRoot;

  for (const segment of suffix) {
    path = path === "/" ? `/${segment}` : `${path}/${segment}`;
    breadcrumbs.push({ label: segment, path });
  }

  return breadcrumbs;
}

export function visibleEditorFileBrowserEntries(
  entries: readonly EditorFileBrowserEntry[],
  options: {
    scope: EditorFileBrowserScope;
    previewCandidateId?: string | null;
  },
): EditorFileBrowserEntry[] {
  return entries.filter((entry) =>
    isEditorFileBrowserPathAllowed(
      entry.path,
      options.scope,
      options.previewCandidateId,
    )
  );
}
